import { forwardRef } from 'react';
import { FormField } from './FormField';

/**
 * Single-input OTP field per the designer's spec. Configured with:
 *   - inputmode="numeric" so the on-screen keyboard shows the number pad
 *   - autocomplete="one-time-code" so iOS/Android can autofill the code
 *     received via SMS/email
 *   - pattern="\d*" + maxLength to keep the value strictly numeric
 *
 * Length defaults to 6 (the API issues 6-digit codes via OTP_LENGTH=6).
 * The input value is filtered to digits-only as the user types so paste-with-
 * spaces still works.
 */
type OtpInputProps = {
  value: string;
  onChange: (next: string) => void;
  length?: number;
  label?: string;
  error?: string | null;
  disabled?: boolean;
  /** Triggered when the user submits via Enter while focused. */
  onSubmit?: () => void;
};

export const OtpInput = forwardRef<HTMLInputElement, OtpInputProps>(function OtpInput(
  { value, onChange, length = 6, label = 'One-time passcode', error, disabled, onSubmit },
  ref,
) {
  return (
    <FormField
      ref={ref}
      label={label}
      visuallyHiddenLabel
      value={value}
      type="text"
      inputMode="numeric"
      pattern="\d*"
      autoComplete="one-time-code"
      maxLength={length}
      placeholder="One Time Passcode"
      disabled={disabled}
      error={error}
      inputClassName="text-center tracking-[0.5em] placeholder:tracking-normal"
      onChange={(e) => {
        const digits = e.target.value.replace(/\D/g, '').slice(0, length);
        onChange(digits);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && onSubmit) {
          e.preventDefault();
          onSubmit();
        }
      }}
    />
  );
});
